import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2, LogOut, Plus, Users, CheckCircle, Clock, Phone, Mail,
  DollarSign, User, Briefcase, Home, Shield, TrendingUp, Search,
  HandshakeIcon, ArrowRight, AlertCircle, XCircle
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'Received': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Assigned to Agent': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Re-assigned': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  'Contacted': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Appointment Set': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'Success': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Did not proceed': 'bg-red-500/20 text-red-300 border-red-500/30',
  'Paid': 'bg-green-500/20 text-green-300 border-green-500/30'
};

const partnerStatusColors = {
  'Pending Confirmation': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Contacted': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Successful': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Unsuccessful': 'bg-red-500/20 text-red-300 border-red-500/30',
};

const businessTypeIcons = { 'Mortgage': Home, 'Insurance': Shield, 'Investments': TrendingUp };
const categoryIcons = { 'Debt Management': DollarSign, 'Home Insurance': Shield, 'Moving Companies': Home, 'Home Inspectors': Search, 'Real Estate': Building2 };

export default function ReferrerDashboard() {
  const navigate = useNavigate();
  const [referrer, setReferrer] = useState(null);
  const [activeTab, setActiveTab] = useState('my-referrals');
  const [loading, setLoading] = useState(true);

  // Existing referral state
  const [referrals, setReferrals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ business_type: '', referral_name: '', referral_email: '', referral_phone: '', notes: '' });

  // Partner referral state
  const [partners, setPartners] = useState([]);
  const [partnerReferrals, setPartnerReferrals] = useState([]);
  const [showPartnerRefModal, setShowPartnerRefModal] = useState(false);
  const [partnerRefSubmitting, setPartnerRefSubmitting] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerRefForm, setPartnerRefForm] = useState({ partner_id: '', client_name: '', client_phone: '', client_email: '', service_needed: '', notes: '' });

  const getToken = () => localStorage.getItem('referrer_token');

  useEffect(() => {
    const token = getToken();
    const referrerData = localStorage.getItem('referrer');
    if (!token || !referrerData) { navigate('/referrer/login'); return; }
    setReferrer(JSON.parse(referrerData));
    fetchAll(token);
  }, [navigate]);

  const fetchAll = async (token) => {
    try {
      const [refRes, partnersRes, myPartnerRefsRes] = await Promise.all([
        axios.get(`${API}/api/referrals/my-referrals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/business-partners/public/list`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/business-partners/referrals/my-partner-referrals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setReferrals(refRes.data);
      setPartners(partnersRes.data);
      setPartnerReferrals(myPartnerRefsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('referrer_token');
        localStorage.removeItem('referrer');
        navigate('/referrer/login');
      }
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('referrer_token');
    localStorage.removeItem('referrer');
    navigate('/referrer/login');
  };

  const handleSubmitReferral = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${API}/api/referrals`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral submitted successfully!');
      setShowModal(false);
      setFormData({ business_type: '', referral_name: '', referral_email: '', referral_phone: '', notes: '' });
      fetchAll(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit referral');
    } finally { setSubmitting(false); }
  };

  const handleCreatePartnerReferral = async (e) => {
    e.preventDefault();
    if (!partnerRefForm.partner_id || !partnerRefForm.client_name) { toast.error('Partner and client name are required'); return; }
    setPartnerRefSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${API}/api/business-partners/referrals/from-agent`, partnerRefForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Partner referral created! The business partner will be notified.');
      setShowPartnerRefModal(false);
      setPartnerRefForm({ partner_id: '', client_name: '', client_phone: '', client_email: '', service_needed: '', notes: '' });
      setSelectedPartner(null);
      fetchAll(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create referral');
    } finally { setPartnerRefSubmitting(false); }
  };

  const openReferModal = (partner) => {
    setSelectedPartner(partner);
    setPartnerRefForm({ partner_id: partner.id, client_name: '', client_phone: '', client_email: '', service_needed: '', notes: '' });
    setShowPartnerRefModal(true);
  };

  const myStats = {
    total: referrals.length,
    pending: referrals.filter(r => !['Success', 'Paid', 'Did not proceed'].includes(r.status)).length,
    successful: referrals.filter(r => ['Success', 'Paid'].includes(r.status)).length,
    paidOut: referrals.filter(r => r.referral_fee_paid).reduce((sum, r) => sum + r.referral_fee_amount, 0)
  };

  const pStats = {
    total: partnerReferrals.length,
    pending: partnerReferrals.filter(r => r.status === 'Pending Confirmation').length,
    contacted: partnerReferrals.filter(r => r.status === 'Contacted').length,
    successful: partnerReferrals.filter(r => r.status === 'Successful').length,
  };

  const filteredPartners = partners.filter(p =>
    !partnerSearch ||
    p.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
    (p.company || '').toLowerCase().includes(partnerSearch.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(partnerSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Utmost Financial Inc.</h1>
              <p className="text-xs text-slate-400">Referral Partner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{referrer?.name}</p>
              <p className="text-xs text-slate-400">{referrer?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="my-referrals" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-400" data-testid="tab-my-referrals">
              My Referrals ({referrals.length})
            </TabsTrigger>
            <TabsTrigger value="partner-referrals" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-400" data-testid="tab-partner-referrals">
              Partner Referrals ({partnerReferrals.length})
            </TabsTrigger>
          </TabsList>

          {/* ===== MY REFERRALS TAB ===== */}
          <TabsContent value="my-referrals" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Users className="w-5 h-5 text-blue-400" /></div><div><p className="text-2xl font-bold text-white" data-testid="stat-total">{myStats.total}</p><p className="text-xs text-slate-400">Total Referrals</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-400" /></div><div><p className="text-2xl font-bold text-white">{myStats.pending}</p><p className="text-xs text-slate-400">In Progress</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-400" /></div><div><p className="text-2xl font-bold text-white">{myStats.successful}</p><p className="text-xs text-slate-400">Successful</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-400" /></div><div><p className="text-2xl font-bold text-white">${myStats.paidOut}</p><p className="text-xs text-slate-400">Earned</p></div></div></CardContent></Card>
            </div>

            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">My Referrals</h2>
              <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-referral-btn">
                    <Plus className="w-4 h-4 mr-2" /> Submit New Referral
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
                  <DialogHeader><DialogTitle>Submit a New Referral</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmitReferral} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Business Type</Label>
                      <Select value={formData.business_type} onValueChange={(v) => setFormData({ ...formData, business_type: v })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select type..." /></SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          <SelectItem value="Mortgage">Mortgage</SelectItem>
                          <SelectItem value="Insurance">Insurance</SelectItem>
                          <SelectItem value="Investments">Investments</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Full Name</Label><Input value={formData.referral_name} onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })} placeholder="John Smith" className="bg-slate-700 border-slate-600" required /></div>
                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.referral_email} onChange={(e) => setFormData({ ...formData, referral_email: e.target.value })} placeholder="john@example.com" className="bg-slate-700 border-slate-600" required /></div>
                    <div className="space-y-2"><Label>Phone</Label><Input type="tel" value={formData.referral_phone} onChange={(e) => setFormData({ ...formData, referral_phone: e.target.value })} placeholder="(204) 555-0123" className="bg-slate-700 border-slate-600" required /></div>
                    <div className="space-y-2"><Label>Notes (Optional)</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional information..." className="bg-slate-700 border-slate-600" rows={3} /></div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitting || !formData.business_type} data-testid="submit-referral-confirm">{submitting ? 'Submitting...' : 'Submit Referral'}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {referrals.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Referrals Yet</h3>
                  <p className="text-slate-400 mb-6">Start earning $200 per successful referral!</p>
                  <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" /> Submit Your First Referral</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {referrals.map((referral) => {
                  const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
                  return (
                    <Card key={referral.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0"><TypeIcon className="w-6 h-6 text-slate-300" /></div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">{referral.referral_name}</h3>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">{referral.business_type}</Badge>
                                <Badge variant="outline" className={statusColors[referral.status]}>{referral.status}</Badge>
                                {referral.referral_fee_paid && <Badge variant="outline" className="bg-green-500/20 text-green-300 border-green-500/30">$200 Paid</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                                <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{referral.referral_email}</span>
                                <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{referral.referral_phone}</span>
                              </div>
                              {referral.assigned_agent_name && <p className="text-sm text-emerald-400 mt-2 flex items-center gap-1"><User className="w-4 h-4" /> Assigned to: {referral.assigned_agent_name}</p>}
                              {referral.notes && <p className="text-sm text-slate-500 mt-2 italic">"{referral.notes}"</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Submitted: {new Date(referral.created_at).toLocaleDateString()}</p>
                            {referral.status === 'Success' && !referral.referral_fee_paid && <p className="text-sm text-amber-400 mt-1">$200 Payment Pending</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ===== PARTNER REFERRALS TAB ===== */}
          <TabsContent value="partner-referrals" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><HandshakeIcon className="w-5 h-5 text-blue-400" /></div><div><p className="text-2xl font-bold text-white" data-testid="partner-stat-total">{pStats.total}</p><p className="text-xs text-slate-400">Total Sent</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-amber-400" /></div><div><p className="text-2xl font-bold text-white">{pStats.pending}</p><p className="text-xs text-slate-400">Pending</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Phone className="w-5 h-5 text-blue-400" /></div><div><p className="text-2xl font-bold text-white">{pStats.contacted}</p><p className="text-xs text-slate-400">Contacted</p></div></div></CardContent></Card>
              <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-400" /></div><div><p className="text-2xl font-bold text-white">{pStats.successful}</p><p className="text-xs text-slate-400">Successful</p></div></div></CardContent></Card>
            </div>

            {/* Browse Partners */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Business Partners</h2>
                <div className="relative w-64">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                  <Input placeholder="Search partners..." value={partnerSearch} onChange={(e) => setPartnerSearch(e.target.value)} className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="partner-search-input" />
                </div>
              </div>
              {filteredPartners.length === 0 ? (
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">No business partners found</p></CardContent></Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {filteredPartners.map(partner => {
                    const CatIcon = categoryIcons[partner.category] || Briefcase;
                    return (
                      <Card key={partner.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`browse-partner-${partner.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center"><CatIcon className="w-4 h-4 text-slate-300" /></div>
                              <div>
                                <h4 className="font-semibold text-sm text-white">{partner.name}</h4>
                                {partner.company && <p className="text-xs text-slate-500">{partner.company}</p>}
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">{partner.category}</Badge>
                          </div>
                          <div className="space-y-1 text-sm text-slate-400 mb-3">
                            {partner.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {partner.phone}</p>}
                            {partner.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {partner.email}</p>}
                          </div>
                          <Button size="sm" className="w-full bg-emerald-600/80 hover:bg-emerald-600 text-white" onClick={() => openReferModal(partner)} data-testid={`refer-to-partner-${partner.id}`}>
                            <ArrowRight className="w-4 h-4 mr-1" /> Refer Client
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* My Partner Referrals */}
            <div>
              <h2 className="text-xl font-bold text-white mb-4">My Partner Referrals</h2>
              {partnerReferrals.length === 0 ? (
                <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><HandshakeIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">No partner referrals yet. Select a partner above to refer a client.</p></CardContent></Card>
              ) : (
                <div className="grid gap-3">
                  {partnerReferrals.map(ref => (
                    <Card key={ref.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`my-partner-referral-${ref.id}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0"><User className="w-5 h-5 text-slate-300" /></div>
                            <div>
                              <h4 className="font-semibold text-white">{ref.client_name}</h4>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <Badge variant="outline" className={partnerStatusColors[ref.status] || 'bg-slate-500/20 text-slate-300'}>{ref.status}</Badge>
                                <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs"><ArrowRight className="w-3 h-3 mr-1" /> {ref.partner_name}</Badge>
                                {ref.partner_category && <Badge variant="outline" className="text-slate-500 border-slate-700 text-xs">{ref.partner_category}</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-400">
                                {ref.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{ref.client_phone}</span>}
                                {ref.client_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{ref.client_email}</span>}
                              </div>
                              {ref.service_needed && <p className="text-xs text-slate-500 mt-1">Service: {ref.service_needed}</p>}
                              {ref.notes && <p className="text-xs text-slate-500 mt-1 italic">"{ref.notes}"</p>}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-slate-500">{ref.referral_date}</p>
                            {ref.confirmed_at && <p className="text-xs text-blue-400 mt-1">Confirmed</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Partner Referral Modal */}
      <Dialog open={showPartnerRefModal} onOpenChange={setShowPartnerRefModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader><DialogTitle>Refer Client to {selectedPartner?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreatePartnerReferral} className="space-y-4 mt-2">
            {selectedPartner && (
              <div className="p-3 bg-slate-700/50 rounded-lg flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span>{selectedPartner.name}</span>
                <Badge variant="outline" className="text-xs border-slate-600">{selectedPartner.category}</Badge>
              </div>
            )}
            <div className="space-y-2"><Label>Client Name *</Label><Input value={partnerRefForm.client_name} onChange={(e) => setPartnerRefForm({ ...partnerRefForm, client_name: e.target.value })} placeholder="Client full name" className="bg-slate-700 border-slate-600" required data-testid="partner-ref-client-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Client Phone</Label><Input type="tel" value={partnerRefForm.client_phone} onChange={(e) => setPartnerRefForm({ ...partnerRefForm, client_phone: e.target.value })} placeholder="(204) 555-0123" className="bg-slate-700 border-slate-600" data-testid="partner-ref-client-phone" /></div>
              <div className="space-y-2"><Label>Client Email</Label><Input type="email" value={partnerRefForm.client_email} onChange={(e) => setPartnerRefForm({ ...partnerRefForm, client_email: e.target.value })} placeholder="client@email.com" className="bg-slate-700 border-slate-600" data-testid="partner-ref-client-email" /></div>
            </div>
            <div className="space-y-2"><Label>Service Needed</Label><Input value={partnerRefForm.service_needed} onChange={(e) => setPartnerRefForm({ ...partnerRefForm, service_needed: e.target.value })} placeholder="e.g. Home Inspection, Debt Consolidation" className="bg-slate-700 border-slate-600" data-testid="partner-ref-service" /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={partnerRefForm.notes} onChange={(e) => setPartnerRefForm({ ...partnerRefForm, notes: e.target.value })} placeholder="Any additional details..." className="bg-slate-700 border-slate-600" rows={3} data-testid="partner-ref-notes" /></div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={partnerRefSubmitting || !partnerRefForm.client_name} data-testid="partner-ref-submit">{partnerRefSubmitting ? 'Creating...' : 'Send Referral'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
